import { Clock3, History, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import Tabs from '../../../components/Tabs/Tabs';
import Table, { StatusPill } from '../../../components/Table/Table';

import usePayrollEntity from '../../../utils/usePayrollEntity';
import { fmtRs, fmtMoney } from '../../../utils/payrollFormat';
import { toIsoDate } from '../../../utils/dateFormat';

const ICON = { size: 15 };

const TABLE_PROPS = {
  search: true,
  searchLabel: 'Search',
  pageSize: 10,
  scrollClassName: '',
};

const statusColumn = {
  key: 'status',
  label: 'Status',
  align: 'center',
  render: (r) => <StatusPill label={r.status} />,
};

/* The two row actions. Quiet until hovered, so a long list of batches does not
   read as a wall of buttons. */
const ROW_BTN =
  'inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[12px] font-semibold transition-colors';

const ApproveButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${ROW_BTN} text-emerald-700 hover:bg-emerald-50`}
  >
    <CheckCircle2 size={14} />
    Approve
  </button>
);

const RejectButton = ({ onClick }) => (
  <button type="button" onClick={onClick} className={`${ROW_BTN} text-red-700 hover:bg-red-50`}>
    <XCircle size={14} />
    Reject
  </button>
);

/* ── Pending Payroll ─────────────────────────────────────────────────────── */
const PendingApprovalTab = () => {
  const { list, loading, save } = usePayrollEntity('pendingPayroll');

  const act = (row, status) => {
    save({ id: row.id, status }, true).then(() =>
      toast.success(`${row.batch} ${status.toLowerCase()}.`, { autoClose: 2000 })
    );
  };

  return (
    <Table
      {...TABLE_PROPS}
      title="Pending Payroll"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['batch', 'submittedBy']}
      searchPlaceholder="Search pending payroll batches..."
      emptyMessage="No payroll batches awaiting approval"
      columns={[
        { key: 'batch', label: 'Batch', grow: true, className: 'font-semibold text-gray-900' },
        { key: 'employeeCount', label: 'Employees', align: 'right', className: 'text-gray-600' },
        {
          key: 'netAmount',
          label: 'Net Amount (Rs)',
          align: 'right',
          className: 'font-semibold text-gray-900',
          render: (r) => fmtMoney(r.netAmount),
        },
        { key: 'submittedBy', label: 'Submitted By', className: 'text-gray-600' },
        {
          key: 'submittedOn',
          label: 'Submitted On',
          align: 'center',
          className: 'text-gray-600 tabular-nums',
          render: (r) => toIsoDate(r.submittedOn),
        },
        statusColumn,
        {
          key: 'actions',
          label: 'Actions',
          align: 'center',
          sortable: false,
          render: (r) => (
            <div className="flex items-center justify-center gap-1">
              <ApproveButton onClick={() => act(r, 'Approved')} />
              <RejectButton onClick={() => act(r, 'Rejected')} />
            </div>
          ),
        },
      ]}
    />
  );
};

/* ── Approval History ────────────────────────────────────────────────────── */
const ApprovalHistoryTab = () => {
  const { list, loading } = usePayrollEntity('approvalHistory');

  return (
    <Table
      {...TABLE_PROPS}
      title="Approval History"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['batch', 'approvedBy']}
      searchPlaceholder="Search approval history..."
      emptyMessage="No approval history found"
      columns={[
        { key: 'batch', label: 'Batch', grow: true, className: 'font-semibold text-gray-900' },
        {
          key: 'action',
          label: 'Action',
          align: 'center',
          render: (r) => <StatusPill label={r.action} />,
        },
        { key: 'approvedBy', label: 'By', className: 'text-gray-600' },
        {
          key: 'date',
          label: 'Date',
          align: 'center',
          className: 'text-gray-600 tabular-nums',
          render: (r) => toIsoDate(r.date),
        },
      ]}
    />
  );
};

/* ── Page ────────────────────────────────────────────────────────────────── */
const PayrollApproval = () => {
  const tabs = [
    { key: 'pending', label: 'Pending Payroll', icon: <Clock3 {...ICON} />, content: <PendingApprovalTab /> },
    { key: 'history', label: 'Approval History', icon: <History {...ICON} />, content: <ApprovalHistoryTab /> },
  ];

  return (
    <div className="p-6 space-y-4">
      <Breadcrumb
        trail={['Payroll', 'Payroll Approval']}
        title="Payroll Approval"
        subtitle="Review, approve or reject payroll batches before payments are released."
      />
      <Tabs tabs={tabs} defaultTab="pending" />
    </div>
  );
};

export default PayrollApproval;